import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getAuth, signOut } from './auth-compat';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import './App.css';
import AddTaskModal from './AddTaskModal.js';
import AddEtapaModal from './AddEtapaModal.js';
import SettingsModal from './SettingsModal.js';
import CommentsModal from './CommentsModal.js';
import DeadlineReminder from './components/DeadlineReminder.js';
import { useDeadlineReminder } from './hooks/useDeadlineReminder.js';
import { db, auth, getCollectionName } from './firebase.js';
import { collection, onSnapshot, doc, addDoc, updateDoc, getDoc, deleteDoc, query, where, getDocs, orderBy, writeBatch } from './firestore-compat';
import { notifyNewTaskAssigned } from './utils/taskNotificationUtils.js';

// ── Funções auxiliares (lógica original) ────────────────────────────────────
const calcularStatusPrazo = (dataFim, status, dataConclusao) => { if (status === 'Feito') { if (dataConclusao && dataFim) { const dataConclusaoObj = new Date(dataConclusao + 'T00:00:00'); const dataFimObj = new Date(dataFim + 'T00:00:00'); if (dataConclusaoObj > dataFimObj) { const diffTime = dataConclusaoObj - dataFimObj; const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); return { texto: `Concluído (${diffDays}d após SLA)`, classe: 'prazo-atrasado' }; } else { return { texto: 'Concluído no prazo', classe: 'prazo-concluido-ok' }; } } return { texto: 'Concluído', classe: 'prazo-concluido-ok' }; } if (!dataFim) { return { texto: '--', classe: 'prazo-ok' }; } const hoje = new Date(); hoje.setHours(0, 0, 0, 0); const dataFimObj = new Date(dataFim + 'T00:00:00'); const diffTime = dataFimObj - hoje; const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)); if (diffDays < 0) return { texto: `Atrasado (${Math.abs(diffDays)}d)`, classe: 'prazo-atrasado' }; if (diffDays === 0) return { texto: 'Termina hoje', classe: 'prazo-urgente' }; return { texto: `Restam ${diffDays}d`, classe: 'prazo-ok' }; };
const calcularDatasDaEtapa = (tarefas) => { if (!tarefas || tarefas.length === 0) { return { inicio: null, fim: null }; } const tarefasComData = tarefas.filter(t => t && t.dataInicio && t.dataFim); if (tarefasComData.length === 0) { return { inicio: null, fim: null }; } const datasInicio = tarefasComData.map(t => new Date(t.dataInicio + 'T00:00:00')); const datasFim = tarefasComData.map(t => new Date(t.dataFim + 'T00:00:00')); const minDataInicio = new Date(Math.min.apply(null, datasInicio)); const maxDataFim = new Date(Math.max.apply(null, datasFim)); return { inicio: minDataInicio.toLocaleDateString(), fim: maxDataFim.toLocaleDateString() }; };
const isEtapaConcluida = (tarefas) => { if (!tarefas || tarefas.length === 0) return false; return tarefas.every(t => t.status === 'Feito'); };

// ── Design helpers ───────────────────────────────────────────────────────────
const GROUP_COLORS = ['#4f46e5','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6'];

const STATUS_CFG = {
  'Feito':        { bg: '#dcfce7', color: '#15803d' },
  'Em andamento': { bg: '#fef9c3', color: '#a16207' },
  'Não iniciado': { bg: '#f1f5f9', color: '#64748b' },
};

const PRIORITY_CFG = {
  'Crítico': { bg: '#fee2e2', color: '#dc2626' },
  'Alta':    { bg: '#ffedd5', color: '#c2410c' },
  'Média':   { bg: '#fef9c3', color: '#a16207' },
  'Baixa':   { bg: '#f0fdf4', color: '#15803d' },
};

const PRAZO_CFG = {
  'prazo-atrasado':     { bg: '#fee2e2', color: '#dc2626' },
  'prazo-urgente':      { bg: '#fff7ed', color: '#c2410c' },
  'prazo-ok':           { bg: '#f0fdf4', color: '#15803d' },
  'prazo-concluido-ok': { bg: '#dcfce7', color: '#15803d' },
};

const getAvatar = (name) => {
  if (!name) return { initials: '?', color: '#94a3b8' };
  const words = name.trim().split(' ').filter(Boolean);
  const initials = words.length >= 2 ? (words[0][0] + words[words.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
  const palette = ['#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#14b8a6'];
  const idx = name.split('').reduce((s, c) => s + c.charCodeAt(0), 0) % palette.length;
  return { initials, color: palette[idx] };
};

const getTimeline = (dataInicio, dataFim, status) => {
  const fmt = (d) => d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  if (!dataInicio || !dataFim) return { pct: 0, label: '--' };
  const start = new Date(dataInicio + 'T00:00:00');
  const end   = new Date(dataFim   + 'T00:00:00');
  if (status === 'Feito') return { pct: 100, label: `${fmt(start)} – ${fmt(end)}` };
  const now   = new Date();
  if (now < start) return { pct: 0, label: `${fmt(start)} – ${fmt(end)}` };
  const total = Math.max(end - start, 1);
  const pct   = Math.min(100, Math.round(((now - start) / total) * 100));
  return { pct, label: `${fmt(start)} – ${fmt(end)}` };
};


function SortableEtapa({ etapa, expandedEtapaId, setExpandedEtapaId, children, groupColor, ...props }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: etapa.id });
  const dndStyle = { transform: CSS.Transform.toString(transform), transition };

  const etapaConcluida = isEtapaConcluida(etapa.tarefas);
  const datasEtapa     = calcularDatasDaEtapa(etapa.tarefas);
  const isExpanded     = expandedEtapaId === etapa.id;
  const tarefaCount    = (etapa.tarefas || []).length;
  const gc             = groupColor || '#4f46e5';

  return (
    <div ref={setNodeRef} style={{ ...dndStyle, marginBottom: 16 }}>
      {/* ── Group header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        background: '#fff', padding: '11px 16px',
        borderRadius: isExpanded ? '10px 10px 0 0' : 10,
        borderLeft: `4px solid ${gc}`,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
        borderBottom: isExpanded ? `1px solid #f0f3f7` : 'none',
      }}>
        <span {...attributes} {...listeners}
          style={{ cursor: 'grab', color: '#cbd5e1', fontSize: '1rem', userSelect: 'none', lineHeight: 1 }}>⠿</span>
        <button
          onClick={() => setExpandedEtapaId(isExpanded ? null : etapa.id)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 0, fontSize: '0.7rem',
            display: 'flex', alignItems: 'center', transition: 'transform 0.2s',
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</button>
        <input
          type="text" value={etapa.text}
          onChange={(e) => props.handleUpdateEtapa(etapa.id, e.target.value)}
          disabled={!props.canEditFields}
          style={{ border: 'none', outline: 'none', fontWeight: 700, fontSize: '0.97rem',
            color: '#1e293b', background: 'transparent', flex: 1, minWidth: 0 }} />
        <span style={{ background: `${gc}15`, color: gc, fontSize: '0.7rem', fontWeight: 700,
          padding: '2px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>
          {tarefaCount} tarefa{tarefaCount !== 1 ? 's' : ''}
        </span>
        {datasEtapa.inicio && (
          <span style={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
            {datasEtapa.inicio} → {datasEtapa.fim}
          </span>
        )}
        {etapaConcluida && (
          <span style={{ fontSize: '0.7rem', background: '#dcfce7', color: '#15803d',
            padding: '2px 9px', borderRadius: 20, fontWeight: 700, whiteSpace: 'nowrap' }}>✓ Concluída</span>
        )}
        {props.isAdmin && (
          <button onClick={() => props.handleDeleteEtapa(etapa.id)}
            style={{ marginLeft: 4, background: '#fee2e2', color: '#dc2626', border: 'none',
              borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
            Apagar
          </button>
        )}
      </div>

      {/* ── Task table ── */}
      {isExpanded && (
        <div style={{ background: '#fff', borderRadius: '0 0 10px 10px',
          boxShadow: '0 2px 6px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          {children}
        </div>
      )}
    </div>
  );
}


function MainApp() {
  const [etapas, setEtapas] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [expandedEtapaId, setExpandedEtapaId] = useState(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isEtapaModalOpen, setIsEtapaModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [isCommentsModalOpen, setIsCommentsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);

  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const currentUser = auth.currentUser;

  const [activeTab, setActiveTab] = useState('projeto'); // 'projeto' | 'sustentacao'

  const allTasksForReminder = useMemo(() => {
    return etapas.flatMap(etapa => etapa.tarefas || []);
  }, [etapas]);

  const { isTodayOpen, isWeekOpen, todayItems, weekItems, closeTodayPopup, closeWeekPopup, dismissToday, openReminderManually } = useDeadlineReminder(allTasksForReminder, 'projeto_iw', 'task');

  const [filtroResponsavel, setFiltroResponsavel] = useState('todos');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroPrazo, setFiltroPrazo] = useState('todos');

  const [ctxMenu, setCtxMenu] = useState({ visible: false, x: 0, y: 0, etapaId: null, tarefa: null });

  const closeCtxMenu = () => setCtxMenu(prev => ({ ...prev, visible: false }));

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') closeCtxMenu(); };
    document.addEventListener('click', closeCtxMenu);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('click', closeCtxMenu); document.removeEventListener('keydown', onKey); };
  }, []);


  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser && currentUser.email) {
        const usersRef = collection(db, getCollectionName('users'));
        const q = query(usersRef, where('email', '==', currentUser.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const userDoc = querySnapshot.docs[0];
          const userData = userDoc.data();
          // Normalizar: roles (array) ou role (string legada)
          const role = Array.isArray(userData.roles) && userData.roles.length > 0
            ? userData.roles[0] : (userData.role || 'leitor');
          setUserRole(role);
          setUserName(userData.name || currentUser.email);
        } else {
          setUserRole('leitor');
        }
      }
      setIsLoading(false);
    };
    fetchUserData();

    const unsubEtapas = onSnapshot(query(collection(db, getCollectionName('etapas')), orderBy('order')), (snapshot) => {
      setEtapas(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    const unsubResponsaveis = onSnapshot(query(collection(db, getCollectionName('responsaveis')), orderBy('nome')), (snapshot) => setResponsaveis(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))));
    return () => { unsubEtapas(); unsubResponsaveis(); };
  }, [currentUser]);

  const isAdmin = userRole === 'admin' || userRole === 'administrador';
  const canEditFields = userRole === 'admin' || userRole === 'administrador';
  const canChangeStatusAndResponsible = userRole === 'admin' || userRole === 'administrador' || userRole === 'comentador';

  const handleAddEtapa = async (nomeNovaEtapa) => {
    const newOrder = etapas.length > 0 ? Math.max(...etapas.map(e => e.order || 0)) + 1 : 0;
    await addDoc(collection(db, getCollectionName('etapas')), {
      text: nomeNovaEtapa,
      tarefas: [],
      order: newOrder
    });
  };

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active.id !== over.id) {
      setEtapas((items) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        const newArray = arrayMove(items, oldIndex, newIndex);
        const batch = writeBatch(db);
        newArray.forEach((etapa, index) => {
          const etapaRef = doc(db, getCollectionName('etapas'), etapa.id);
          batch.update(etapaRef, { order: index });
        });
        batch.commit();
        return newArray;
      });
    }
  }

  const handleLogout = () => { signOut(getAuth()); };
  const handleAddTask = async (etapaId, newTask) => { 
    const etapaRef = doc(db, getCollectionName('etapas'), etapaId); 
    const etapaDoc = await getDoc(etapaRef); 
    if (etapaDoc.exists()) { 
      const tarefasAtuais = etapaDoc.data().tarefas || []; 
      await updateDoc(etapaRef, { tarefas: [...tarefasAtuais, newTask] }); 
      if (newTask.responsavel) {
          notifyNewTaskAssigned(newTask.text, newTask.responsavel, userName, 'Projeto IW');
      }
    } 
  };
  const handleDeleteTask = async (etapaId, tarefaId) => { const etapaRef = doc(db, getCollectionName('etapas'), etapaId); const etapaDoc = await getDoc(etapaRef); if (etapaDoc.exists()) { const tarefasAtuais = etapaDoc.data().tarefas || []; const tarefasAtualizadas = tarefasAtuais.filter(t => t && t.id !== tarefaId); await updateDoc(etapaRef, { tarefas: tarefasAtualizadas }); } };

  const handleMoveTask = async (currentEtapaId, targetEtapaId, task) => {
    if (!targetEtapaId || currentEtapaId === targetEtapaId) return;

    // 1. Remover da etapa atual
    const currentEtapaRef = doc(db, getCollectionName('etapas'), currentEtapaId);
    const currentEtapaDoc = await getDoc(currentEtapaRef);
    if (currentEtapaDoc.exists()) {
      const tarefasAtuais = currentEtapaDoc.data().tarefas || [];
      await updateDoc(currentEtapaRef, { tarefas: tarefasAtuais.filter(t => t.id !== task.id) });
    }

    // 2. Adicionar na nova etapa
    const targetEtapaRef = doc(db, getCollectionName('etapas'), targetEtapaId);
    const targetEtapaDoc = await getDoc(targetEtapaRef);
    if (targetEtapaDoc.exists()) {
      const tarefasAlvo = targetEtapaDoc.data().tarefas || [];
      await updateDoc(targetEtapaRef, { tarefas: [...tarefasAlvo, task] });
    }
  };

  // --- FUNÇÃO DE ATUALIZAÇÃO LIMPA (SEM VALIDAÇÃO) ---
  const handleUpdateTask = async (etapaId, tarefaId, campo, valor) => {
    const etapaRef = doc(db, getCollectionName('etapas'), etapaId);
    const etapaDoc = await getDoc(etapaRef);
    if (etapaDoc.exists()) {
      const tarefasAtuais = etapaDoc.data().tarefas || [];
      let tarefasAtualizadas = tarefasAtuais.map(t => {
        if (t && t.id === tarefaId) {
          const tarefaModificada = { ...t, [campo]: valor };

          if (campo === 'status') {
            if (valor === 'Feito' && !t.dataConclusao) {
              tarefaModificada.dataConclusao = new Date().toISOString().split('T')[0];
            } else if (valor !== 'Feito') {
              tarefaModificada.dataConclusao = null;
            }
          } else if (campo === 'responsavel' && t.responsavel !== valor && valor) {
              notifyNewTaskAssigned(t.text || 'Tarefa', valor, userName, 'Projeto IW');
          }
          return tarefaModificada;
        }
        return t;
      });
      await updateDoc(etapaRef, { tarefas: tarefasAtualizadas });
    }
  };

  const handleDeleteEtapa = async (etapaId) => { if (window.confirm("Tem certeza que deseja apagar esta etapa e todas as suas tarefas?")) { await deleteDoc(doc(db, getCollectionName('etapas'), etapaId)); } };
  const handleUpdateEtapa = async (etapaId, novoNome) => { const etapaRef = doc(db, getCollectionName('etapas'), etapaId); await updateDoc(etapaRef, { text: novoNome }); };
  const handleAddComment = async (etapaId, tarefaId, commentText) => { const etapaRef = doc(db, getCollectionName('etapas'), etapaId); const etapaDoc = await getDoc(etapaRef); if (etapaDoc.exists()) { const tarefasAtuais = etapaDoc.data().tarefas || []; const newComment = { text: commentText, userName: userName, userEmail: currentUser.email, timestamp: Date.now() }; const tarefasAtualizadas = tarefasAtuais.map(t => { if (t && t.id === tarefaId) { const comments = t.comments || []; setSelectedTask(prevTask => ({ ...prevTask, comments: [...comments, newComment] })); return { ...t, comments: [...comments, newComment] }; } return t; }); await updateDoc(etapaRef, { tarefas: tarefasAtualizadas }); } };
  const openCommentsModal = (etapaId, task) => { setSelectedTask({ ...task, etapaId }); setIsCommentsModalOpen(true); };

  // --- LÓGICA DE FILTRAGEM (SEM ORDENAÇÃO) ---
  const filteredEtapas = useMemo(() => {
    let etapasParaExibir = etapas;

    if (filtroResponsavel !== 'todos' || filtroStatus !== 'todos' || filtroPrazo !== 'todos') {
      etapasParaExibir = etapas.map(etapa => {
        const tarefasFiltradas = (etapa.tarefas || []).filter(tarefa => {
          if (!tarefa) return false;

          const responsavelMatch = filtroResponsavel === 'todos' || tarefa.responsavel === filtroResponsavel;
          const statusMatch = filtroStatus === 'todos' || tarefa.status === filtroStatus;

          const statusPrazo = calcularStatusPrazo(tarefa.dataFim, tarefa.status, tarefa.dataConclusao);
          let prazoMatch = true;
          if (filtroPrazo === 'atrasado') {
            prazoMatch = statusPrazo.classe === 'prazo-atrasado';
          } else if (filtroPrazo === 'urgente') {
            prazoMatch = statusPrazo.classe === 'prazo-urgente';
          } else if (filtroPrazo === 'no_prazo') {
            prazoMatch = statusPrazo.classe === 'prazo-ok' || statusPrazo.classe === 'prazo-concluido-ok';
          }

          return responsavelMatch && statusMatch && prazoMatch;
        });
        return { ...etapa, tarefas: tarefasFiltradas };
      });
    }

    return etapasParaExibir;
  }, [etapas, filtroResponsavel, filtroStatus, filtroPrazo]);


  if (isLoading) { return <div>Carregando permissões...</div>; }



  return (
    <div className="App">

      {/* ── Toolbar ── */}
      <div style={{ padding: '0 24px 0', marginBottom: 0 }}>
        {/* Top row: title + tabs + actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>

          {/* Título */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>
              Projeto IW
            </h2>
            {/* Tabs */}
            <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: 10, padding: 3, gap: 2 }}>
              {[
                { key: 'projeto',     label: '📋 Projeto IW',      color: '#6366f1' },
              ].map(tab => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                  style={{
                    border: 'none', borderRadius: 8, padding: '6px 16px', cursor: 'pointer',
                    fontSize: '0.82rem', fontWeight: 700, transition: 'all 0.15s',
                    background: activeTab === tab.key ? '#fff' : 'transparent',
                    color: activeTab === tab.key ? tab.color : '#94a3b8',
                    boxShadow: activeTab === tab.key ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                  }}>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ flex: 1 }} />

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={openReminderManually}
              style={{ display: 'flex', alignItems: 'center', gap: 7, background: '#fff7ed',
                border: '1px solid #fed7aa', borderRadius: 8, padding: '7px 14px',
                cursor: 'pointer', fontSize: '0.82rem', color: '#c2410c', fontWeight: 700 }}
              onMouseEnter={e => e.currentTarget.style.background = '#ffedd5'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff7ed'}>
              🔔 Vencimentos
            </button>
            {isAdmin && (
              <button onClick={() => setIsSettingsModalOpen(true)}
                style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
                  padding: '7px 10px', cursor: 'pointer', fontSize: '1rem', color: '#64748b', lineHeight: 1 }}
                title="Configurações"
                onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.background = '#f8fafc'}>
                ⚙️
              </button>
            )}
            {isAdmin && activeTab === 'projeto' && (
              <button onClick={() => setIsEtapaModalOpen(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#f0fdf4',
                  border: '1px solid #bbf7d0', borderRadius: 8, padding: '7px 14px',
                  cursor: 'pointer', fontSize: '0.82rem', color: '#15803d', fontWeight: 700 }}
                onMouseEnter={e => e.currentTarget.style.background = '#dcfce7'}
                onMouseLeave={e => e.currentTarget.style.background = '#f0fdf4'}>
                + Etapa
              </button>
            )}
            {isAdmin && activeTab === 'projeto' && (
              <button onClick={() => setIsTaskModalOpen(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#6366f1',
                  border: 'none', borderRadius: 8, padding: '7px 16px',
                  cursor: 'pointer', fontSize: '0.82rem', color: '#fff', fontWeight: 700,
                  boxShadow: '0 2px 8px rgba(99,102,241,0.3)' }}
                onMouseEnter={e => e.currentTarget.style.background = '#4f46e5'}
                onMouseLeave={e => e.currentTarget.style.background = '#6366f1'}>
                + Tarefa
              </button>
            )}
          </div>
        </div>

        {/* Filter row — só aparece na aba Projeto IW */}
        {activeTab === 'projeto' && <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
          background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: 20, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase',
            letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>Filtros</span>
          <div style={{ width: 1, height: 20, background: '#e2e8f0' }} />

          {[
            { id: 'resp', label: '👤 Responsável', value: filtroResponsavel, onChange: setFiltroResponsavel,
              options: [{ v: 'todos', l: 'Todos' }, ...responsaveis.map(r => ({ v: r.nome, l: r.nome }))] },
            { id: 'status', label: '🏷 Status', value: filtroStatus, onChange: setFiltroStatus,
              options: [{ v: 'todos', l: 'Todos' }, { v: 'Não iniciado', l: 'Não iniciado' }, { v: 'Em andamento', l: 'Em andamento' }, { v: 'Feito', l: 'Feito' }] },
            { id: 'prazo', label: '📅 Prazo', value: filtroPrazo, onChange: setFiltroPrazo,
              options: [{ v: 'todos', l: 'Todos' }, { v: 'no_prazo', l: 'No prazo' }, { v: 'urgente', l: 'Termina hoje' }, { v: 'atrasado', l: 'Atrasado' }] },
          ].map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>{f.label}</label>
              <select value={f.value} onChange={e => f.onChange(e.target.value)}
                style={{ border: `1px solid ${f.value !== 'todos' ? '#6366f1' : '#e2e8f0'}`,
                  borderRadius: 7, padding: '5px 10px', fontSize: '0.8rem',
                  color: f.value !== 'todos' ? '#4f46e5' : '#334155',
                  background: f.value !== 'todos' ? '#eef2ff' : '#f8fafc',
                  fontWeight: f.value !== 'todos' ? 700 : 400,
                  cursor: 'pointer', outline: 'none' }}>
                {f.options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
          ))}

          {(filtroResponsavel !== 'todos' || filtroStatus !== 'todos' || filtroPrazo !== 'todos') && (
            <button
              onClick={() => { setFiltroResponsavel('todos'); setFiltroStatus('todos'); setFiltroPrazo('todos'); }}
              style={{ marginLeft: 'auto', background: 'none', border: '1px solid #fca5a5', borderRadius: 7,
                padding: '5px 12px', cursor: 'pointer', fontSize: '0.78rem', color: '#ef4444', fontWeight: 600 }}>
              ✕ Limpar
            </button>
          )}
        </div>}
      </div>

      {isAdmin && <SettingsModal isOpen={isSettingsModalOpen} onClose={() => setIsSettingsModalOpen(false)} />}
      {isAdmin && <AddEtapaModal isOpen={isEtapaModalOpen} onClose={() => setIsEtapaModalOpen(false)} onAddEtapa={handleAddEtapa} />}
      {isAdmin && <AddTaskModal isOpen={isTaskModalOpen} onClose={() => setIsTaskModalOpen(false)} onAddTask={handleAddTask} etapas={etapas} responsaveis={responsaveis} />}

      <CommentsModal
        isOpen={isCommentsModalOpen}
        onClose={() => setIsCommentsModalOpen(false)}
        task={selectedTask}
        onAddComment={handleAddComment}
      />

      {/* ── Projeto IW (board) ── */}
      {activeTab === 'projeto' && <main className="task-container">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={etapas.map(e => e.id)} // O DndContext só precisa dos IDs das etapas
            strategy={verticalListSortingStrategy}
          >
            {filteredEtapas.map((etapa, etapaIdx) => {
              const gc = GROUP_COLORS[etapas.findIndex(e => e.id === etapa.id) % GROUP_COLORS.length] || GROUP_COLORS[0];
              // Grid columns: tarefa | responsavel | status | timeline | prazo | prioridade | inicio | fim | conclusão | ações
              const GRID = 'minmax(160px,2fr) 130px 148px 160px 110px 88px 108px 108px 108px 100px';
              const thStyle = { padding: '6px 10px', fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8',
                textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: '1px solid #f0f3f7' };
              return (
                <SortableEtapa
                  key={etapa.id}
                  etapa={etapa}
                  groupColor={gc}
                  expandedEtapaId={expandedEtapaId}
                  setExpandedEtapaId={setExpandedEtapaId}
                  handleUpdateEtapa={handleUpdateEtapa}
                  handleDeleteEtapa={handleDeleteEtapa}
                  canEditFields={canEditFields}
                  isAdmin={isAdmin}
                >
                  {/* ── Column header ── */}
                  {(etapa.tarefas && etapa.tarefas.length > 0) && (
                    <div style={{ display: 'grid', gridTemplateColumns: GRID, background: '#fafbfc', borderBottom: '1px solid #f0f3f7' }}>
                      <div style={thStyle}>Tarefa</div>
                      <div style={thStyle}>Responsável</div>
                      <div style={thStyle}>Status</div>
                      <div style={thStyle}>Timeline</div>
                      <div style={thStyle}>Prazo</div>
                      <div style={thStyle}>Prioridade</div>
                      <div style={thStyle}>Início</div>
                      <div style={thStyle}>Fim</div>
                      <div style={thStyle}>Conclusão</div>
                      <div style={thStyle}></div>
                    </div>
                  )}

                  {/* ── Task rows ── */}
                  {etapa.tarefas && etapa.tarefas
                    .slice().sort((a, b) => {
                      if (!a?.dataInicio && !b?.dataInicio) return 0;
                      if (!a?.dataInicio) return 1;
                      if (!b?.dataInicio) return -1;
                      return new Date(a.dataInicio) - new Date(b.dataInicio);
                    })
                    .map((tarefa, index) => {
                      if (!tarefa) return null;
                      const statusPrazo = calcularStatusPrazo(tarefa.dataFim, tarefa.status, tarefa.dataConclusao);
                      const commentCount = tarefa.comments ? tarefa.comments.length : 0;
                      const sCfg   = STATUS_CFG[tarefa.status]   || STATUS_CFG['Não iniciado'];
                      const pCfg   = PRIORITY_CFG[tarefa.prioridade] || PRIORITY_CFG['Média'];
                      const prCfg  = PRAZO_CFG[statusPrazo.classe] || PRAZO_CFG['prazo-ok'];
                      const avatar = getAvatar(tarefa.responsavel);
                      const tl     = getTimeline(tarefa.dataInicio, tarefa.dataFim, tarefa.status);

                      const handleCtxMenu = (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCtxMenu({ visible: true, x: e.clientX, y: e.clientY, etapaId: etapa.id, tarefa });
                      };

                      const rowBase = {
                        display: 'grid', gridTemplateColumns: GRID,
                        alignItems: 'center',
                        borderBottom: '1px solid #f4f6f8',
                        background: index % 2 === 0 ? '#fff' : '#fafbfc',
                        transition: 'background 0.12s',
                        cursor: 'context-menu',
                      };
                      const cellPad = { padding: '8px 10px' };
                      const inpBase = {
                        border: 'none', outline: 'none', background: 'transparent',
                        fontSize: '0.85rem', color: '#1e293b', width: '100%',
                        fontFamily: 'inherit',
                      };
                      const dateInp = {
                        ...inpBase, fontSize: '0.78rem', color: '#64748b',
                        cursor: canEditFields ? 'pointer' : 'default',
                      };

                      return (
                        <div key={tarefa.id || index} style={rowBase}
                          onContextMenu={handleCtxMenu}
                          onMouseEnter={e => { e.currentTarget.style.background = '#f0f4ff'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = index % 2 === 0 ? '#fff' : '#fafbfc'; }}>

                          {/* TAREFA */}
                          <div style={{ ...cellPad, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                            <span style={{ width: 3, height: 32, borderRadius: 2, background: gc, flexShrink: 0 }} />
                            <input type="text" value={tarefa.text || ''}
                              onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'text', e.target.value)}
                              style={{ ...inpBase, fontWeight: 500 }} disabled={!canEditFields} />
                          </div>

                          {/* RESPONSÁVEL */}
                          <div style={{ ...cellPad, display: 'flex', alignItems: 'center', gap: 7, position: 'relative' }}>
                            {tarefa.responsavel
                              ? <div style={{ width: 28, height: 28, borderRadius: '50%', background: avatar.color,
                                  color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '0.68rem', fontWeight: 700, flexShrink: 0 }}>{avatar.initials}</div>
                              : <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#e2e8f0',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '0.8rem', flexShrink: 0 }}>?</div>
                            }
                            <select value={tarefa.responsavel || ''}
                              onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'responsavel', e.target.value)}
                              disabled={!canChangeStatusAndResponsible}
                              style={{ ...inpBase, fontSize: '0.8rem', cursor: canChangeStatusAndResponsible ? 'pointer' : 'default',
                                overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              <option value="">— sem responsável —</option>
                              {responsaveis.map(r => <option key={r.id} value={r.nome}>{r.nome}</option>)}
                            </select>
                          </div>

                          {/* STATUS */}
                          <div style={cellPad}>
                            <div style={{ display: 'inline-flex', borderRadius: 20, background: sCfg.bg,
                              overflow: 'hidden', maxWidth: '100%' }}>
                              <select value={tarefa.status || 'Não iniciado'}
                                onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'status', e.target.value)}
                                disabled={!canChangeStatusAndResponsible}
                                style={{ background: 'transparent', border: 'none', outline: 'none',
                                  color: sCfg.color, fontWeight: 700, fontSize: '0.78rem',
                                  padding: '4px 12px', cursor: canChangeStatusAndResponsible ? 'pointer' : 'default',
                                  appearance: 'none', WebkitAppearance: 'none' }}>
                                <option value="Não iniciado">Não iniciado</option>
                                <option value="Em andamento">Em andamento</option>
                                <option value="Feito">Feito</option>
                              </select>
                            </div>
                          </div>

                          {/* TIMELINE */}
                          <div style={{ ...cellPad, minWidth: 0 }}>
                            <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tl.label}</div>
                            <div style={{ height: 6, borderRadius: 3, background: '#e2e8f0', overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${tl.pct}%`, borderRadius: 3,
                                background: tarefa.status === 'Feito' ? '#22c55e' : tl.pct > 80 ? '#ef4444' : gc,
                                transition: 'width 0.3s' }} />
                            </div>
                          </div>

                          {/* PRAZO */}
                          <div style={cellPad}>
                            <span style={{ background: prCfg.bg, color: prCfg.color, fontSize: '0.75rem',
                              fontWeight: 600, padding: '3px 9px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                              {statusPrazo.texto}
                            </span>
                          </div>

                          {/* PRIORIDADE */}
                          <div style={cellPad}>
                            <div style={{ display: 'inline-flex', borderRadius: 20, background: pCfg.bg, overflow: 'hidden' }}>
                              {canEditFields
                                ? <select value={tarefa.prioridade || 'Média'}
                                    onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'prioridade', e.target.value)}
                                    style={{ background: 'transparent', border: 'none', outline: 'none',
                                      color: pCfg.color, fontWeight: 700, fontSize: '0.75rem',
                                      padding: '4px 10px', cursor: 'pointer',
                                      appearance: 'none', WebkitAppearance: 'none' }}>
                                    <option value="Baixa">Baixa</option>
                                    <option value="Média">Média</option>
                                    <option value="Alta">Alta</option>
                                    <option value="Crítico">Crítico</option>
                                  </select>
                                : <span style={{ color: pCfg.color, fontWeight: 700, fontSize: '0.75rem', padding: '4px 10px' }}>
                                    {tarefa.prioridade || 'Média'}
                                  </span>
                              }
                            </div>
                          </div>

                          {/* INÍCIO */}
                          <div style={cellPad}>
                            <input type="date" value={tarefa.dataInicio || ''}
                              onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'dataInicio', e.target.value)}
                              style={dateInp} disabled={!canEditFields} />
                          </div>

                          {/* FIM */}
                          <div style={cellPad}>
                            <input type="date" value={tarefa.dataFim || ''}
                              onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'dataFim', e.target.value)}
                              style={dateInp} disabled={!canEditFields} />
                          </div>

                          {/* CONCLUSÃO */}
                          <div style={cellPad}>
                            {tarefa.status === 'Feito'
                              ? <input type="date" value={tarefa.dataConclusao || ''}
                                  onChange={(e) => handleUpdateTask(etapa.id, tarefa.id, 'dataConclusao', e.target.value)}
                                  style={dateInp} disabled={!canEditFields} />
                              : <span style={{ fontSize: '0.78rem', color: '#cbd5e1' }}>—</span>
                            }
                          </div>

                          {/* AÇÕES */}
                          <div style={{ ...cellPad, display: 'flex', alignItems: 'center', gap: 6 }}>
                            <button onClick={() => openCommentsModal(etapa.id, tarefa)}
                              style={{ background: commentCount > 0 ? '#eff6ff' : '#f8fafc',
                                border: `1px solid ${commentCount > 0 ? '#bfdbfe' : '#e2e8f0'}`,
                                borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                                fontSize: '0.75rem', color: commentCount > 0 ? '#3b82f6' : '#94a3b8',
                                fontWeight: commentCount > 0 ? 700 : 400, display: 'flex', alignItems: 'center', gap: 3 }}>
                              💬 {commentCount}
                            </button>
                            <span style={{ fontSize: '0.68rem', color: '#cbd5e1', userSelect: 'none' }}
                              title="Clique direito para mais opções">⋯</span>
                          </div>
                        </div>
                      );
                    })}
                </SortableEtapa>
              );
            })}
          </SortableContext>
        </DndContext>
      </main>}
      {/* ── Context Menu ── */}
      {ctxMenu.visible && (
        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'fixed', top: ctxMenu.y, left: ctxMenu.x, zIndex: 9999,
            background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
            border: '1px solid #e2e8f0', minWidth: 220, overflow: 'hidden',
            animation: 'fadeInDown 0.12s ease',
          }}>
          {/* Header */}
          <div style={{ padding: '8px 14px 6px', borderBottom: '1px solid #f0f3f7' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Opções da tarefa
            </div>
            <div style={{ fontSize: '0.82rem', color: '#1e293b', fontWeight: 600, marginTop: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190 }}>
              {ctxMenu.tarefa?.text || 'Tarefa'}
            </div>
          </div>

          {/* Comentários */}
          <button onClick={() => { openCommentsModal(ctxMenu.etapaId, ctxMenu.tarefa); closeCtxMenu(); }}
            style={{ width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
              textAlign: 'left', fontSize: '0.85rem', color: '#3b82f6', display: 'flex', alignItems: 'center', gap: 8 }}
            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}>
            💬 <span>Ver comentários <span style={{ color: '#94a3b8', fontSize: '0.78rem' }}>({ctxMenu.tarefa?.comments?.length || 0})</span></span>
          </button>

          {/* Mover para */}
          {canEditFields && etapas.filter(e => e.id !== ctxMenu.etapaId).length > 0 && (
            <>
              <div style={{ borderTop: '1px solid #f0f3f7', padding: '6px 14px 4px',
                fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Mover para
              </div>
              {etapas.filter(e => e.id !== ctxMenu.etapaId).map(e => (
                <button key={e.id}
                  onClick={() => { handleMoveTask(ctxMenu.etapaId, e.id, ctxMenu.tarefa); closeCtxMenu(); }}
                  style={{ width: '100%', padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer',
                    textAlign: 'left', fontSize: '0.84rem', color: '#334155', display: 'flex', alignItems: 'center', gap: 8 }}
                  onMouseEnter={ev => ev.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={ev => ev.currentTarget.style.background = 'none'}>
                  ↗ {e.text}
                </button>
              ))}
            </>
          )}

          {/* Excluir */}
          {isAdmin && (
            <>
              <div style={{ borderTop: '1px solid #f0f3f7' }} />
              <button
                onClick={() => { handleDeleteTask(ctxMenu.etapaId, ctxMenu.tarefa?.id); closeCtxMenu(); }}
                style={{ width: '100%', padding: '9px 14px', background: 'none', border: 'none', cursor: 'pointer',
                  textAlign: 'left', fontSize: '0.84rem', color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8 }}
                onMouseEnter={e => e.currentTarget.style.background = '#fff5f5'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                🗑️ Excluir tarefa
              </button>
            </>
          )}
        </div>
      )}

      <DeadlineReminder
        isTodayOpen={isTodayOpen}
        isWeekOpen={isWeekOpen}
        todayItems={todayItems}
        weekItems={weekItems}
        onCloseTodayPopup={closeTodayPopup}
        onCloseWeekPopup={closeWeekPopup}
        onDismissToday={dismissToday}
        type="task"
      />
    </div>
  );
}

export default MainApp;